import { computed, type ComputedRef } from "vue";
import { useTheme } from "vuetify";

type TPlaceholderVariant = "default" | "error";

const buildPlaceholder = (c: Record<string, string>, variant: TPlaceholderVariant = "default") => {
  const isError = variant === "error";

  const bg1 = isError
    ? (c.errorLight ?? c.errorContainer ?? "#FDECEC")
    : (c.background ?? "#F2F2F8");

  const bg2 = c.surface ?? "#ffffff";

  const stroke = isError ? (c.error ?? "#E53935") : (c.liteDef3 ?? c.liteDef2 ?? "#e1e2ec");

  const accent = isError ? (c.error ?? "#E53935") : (c.secondary ?? "#009C89");

  const accent2 = isError
    ? (c.errorDark ?? c.onErrorContainer ?? c.primary ?? "#B3261E")
    : (c.primaryLighter ?? c.primary ?? "#253D4E");

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 400">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="${bg1}"/>
          <stop offset="1" stop-color="${bg2}"/>
        </linearGradient>

        <linearGradient id="accent" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stop-color="${accent}"/>
          <stop offset="1" stop-color="${accent2}"/>
        </linearGradient>
      </defs>

      <rect width="600" height="400" fill="url(#bg)"/>
      <rect
        x="120"
        y="120"
        width="360"
        height="200"
        rx="18"
        fill="none"
        stroke="${stroke}"
        stroke-width="3"
        opacity="0.9"
      />
      <circle
        cx="190"
        cy="175"
        r="20"
        fill="url(#accent)"
        opacity="0.35"
      />
      <path
        d="M165 300 L265 205 L335 285 L390 235 L460 300 Z"
        fill="url(#accent)"
        opacity="0.22"
      />
    </svg>
  `;

  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

export function useDefaultPlaceholder(): ComputedRef<string> {
  const theme = useTheme();

  return computed(() => {
    const c = theme.current.value.colors as Record<string, string>;
    return buildPlaceholder(c, "default");
  });
}

export function useErrorPlaceholder(): ComputedRef<string> {
  const theme = useTheme();

  return computed(() => {
    const c = theme.current.value.colors as Record<string, string>;
    return buildPlaceholder(c, "error");
  });
}
